import React, { useState } from "react"
import styled from "styled-components"

import { ReactComponent as DownIcon } from "../../../images/icons/chevron-down.svg"
import { ReactComponent as StockIcon } from "../../../images/icons/warehouse.svg"
import { IncomingStock, Maybe, Stock } from "lib/types/generated/graphql-types"
import InventoryRow from "./InventoryRow"
import Status from "../../Ui/Status"
import { TooltipIcon } from "../../Ui/TooltipIcon"
import { useLazyQuery } from "@apollo/client"
import GET_INCOMING_STOCK from "../../../graphql/queries/incomingStock/GetIncomingStock"
import dayjs from "dayjs"
import { IncomingWrapper } from "../Shared.styled"
import { WithAuth } from "../../Auth"
import { PermissionGroups } from "../../../routes/constants"

type StyledProps = {
  $expanded: boolean
}

const Arrow = styled(DownIcon)<StyledProps>`
  width: 1.2rem !important;
  margin-left: 1rem;
  transition: transform 0.2s;
  transform: rotate(${(p) => (p.$expanded ? "180deg" : "0deg")});
`

const ValidateStock = styled.div`
  margin-bottom: 1rem;
  font-weight: 700;
  display: flex;

  i {
    width: 2rem;
  }

  span {
    margin-left: 1rem;
  }
`

const Tab = styled.div<StyledProps>`
  display: flex;
  align-items: center;
  border-bottom: ${(p) => (p.$expanded ? "none" : `0.1rem solid ${p.theme.colors.greyLight}`)};

  svg {
    width: 1.6rem;
    margin-right: 1rem;
  }

  &:hover {
    cursor: pointer;
    color: ${(p) => p.theme.colors.turquoiseDark};

    svg {
      fill: ${(p) => p.theme.colors.turquoiseDark};
    }
  }
`

type Props = {
  stock: Maybe<Stock> | undefined
  variantId: string
}

export const ProductVariantStock = ({ stock, variantId }: Props) => {
  const [expanded, setExpanded] = useState<boolean>(false)
  const [incomingStock, setIncomingStock] = useState<IncomingStock[]>([])

  const [fetchIncomingStock] = useLazyQuery(GET_INCOMING_STOCK, {
    fetchPolicy: "network-only"
  })

  const handleExpand = () => {
    if (!expanded) {
      fetchIncomingStock({ variables: { productVariantId: variantId } }).then(({ data }) =>
        setIncomingStock(data?.getIncomingStock ?? [])
      )
    }
    setExpanded(!expanded)
  }

  const recentStockDataList = incomingStock
    .filter((stock) => !!stock.incomingStockDate)
    .filter((stock) => dayjs(stock.incomingStockDate).isAfter(dayjs()))
    .sort((a, b) => dayjs(a.incomingStockDate).valueOf() - dayjs(b.incomingStockDate).valueOf())

  const recentStock = recentStockDataList.length > 0 ? recentStockDataList[0] : undefined

  const toValidateStock = (
    stockData: Maybe<Stock> | undefined,
    recentStock: IncomingStock | undefined
  ) => {
    if (stockData !== null || recentStock !== undefined) {
      return (
        <>
          <ValidateStock>
            Validate stock:
            {stockData?.validateStock ? (
              <Status status={"Enabled"} />
            ) : (
              <Status status={"Disabled"} />
            )}
            <TooltipIcon content="Validation of stock quantity in cart to prevent overselling" />
          </ValidateStock>
          {stockData?.inventories.map((inventory) => (
            <InventoryRow
              key={inventory.inventoryId}
              inventoryId={inventory.inventoryId}
              stock={inventory.quantity}
              created={inventory.created}
              updated={inventory.updated}
            />
          ))}
          {recentStock && (
            <WithAuth
              requiredGroups={PermissionGroups.products.read}
              configKey={"incomingStockEnabled"}
            >
              <IncomingWrapper>
                <div>
                  <b>Incoming quantity:</b> {recentStock.incomingStockQuantity}
                </div>
                <div>
                  <b>Incoming date:</b> {recentStock.incomingStockDate}
                </div>
              </IncomingWrapper>
            </WithAuth>
          )}
        </>
      )
    }
    return <Status status={"Missing stock data"} />
  }

  return (
    <>
      <Tab onClick={handleExpand} $expanded={expanded}>
        <StockIcon />
        <h4>Stock: {stock?.stockQuantity}</h4>
        <Arrow $expanded={expanded} />
      </Tab>
      {expanded && <>{toValidateStock(stock, recentStock)}</>}
    </>
  )
}
